import { useCallback, useEffect, useState } from 'react'

import { BANNER_HEIGHT, Banner } from './Banner'

type BannerConfig = {
  title: string
  description: string | null
  link: string
  linkLabel: string
  endDate: string | null
}

// Steered from the "Website Banner" singleton in the Sanity studio. Fetched
// from the browser (not during SSR) so a Sanity outage can never slow down or
// break page rendering — worst case the banner doesn't show.
const SANITY_QUERY_URL =
  'https://dnsgqii5.apicdn.sanity.io/v2024-10-24/data/query/production?query=' +
  encodeURIComponent(
    '*[_type == "websiteBanner" && _id == "websiteBanner" && enabled == true][0]{title, description, link, linkLabel, endDate}'
  )

const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000 // 24h

// Versioned by link so a new campaign re-enables the banner for everyone.
const dismissKey = (config: BannerConfig) => `banner.dismissed:${config.link}`

// Fails closed: a malformed endDate counts as expired.
const isExpired = (config: BannerConfig) => {
  if (config.endDate == null) return false
  const endsAt = new Date(config.endDate).getTime()
  return !Number.isFinite(endsAt) || Date.now() >= endsAt
}

const isDismissed = (config: BannerConfig) => {
  try {
    const stored = localStorage.getItem(dismissKey(config))
    if (!stored) return false
    const expiresAt = Number(stored)
    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) return true
    localStorage.removeItem(dismissKey(config))
    return false
  } catch {
    // localStorage can throw (private mode, disabled storage). Show anyway.
    return false
  }
}

const isValidLink = (link: unknown): link is string => {
  if (typeof link !== 'string') return false
  try {
    return ['http:', 'https:'].includes(new URL(link).protocol)
  } catch {
    return false
  }
}

const parseBannerConfig = (result: unknown): BannerConfig | null => {
  if (result == null || typeof result !== 'object') return null
  const { title, description, link, linkLabel, endDate } = result as Record<
    string,
    unknown
  >
  if (
    typeof title !== 'string' ||
    typeof linkLabel !== 'string' ||
    !isValidLink(link)
  ) {
    return null
  }
  return {
    title,
    link,
    linkLabel,
    description: typeof description === 'string' ? description : null,
    endDate: typeof endDate === 'string' ? endDate : null,
  }
}

// Shared across hook instances (SlitePage and HelpLayout both call useBanner)
// so a page never fetches the banner twice.
let bannerPromise: Promise<BannerConfig | null> | null = null

const fetchBannerConfig = () => {
  bannerPromise ??= fetch(SANITY_QUERY_URL, {
    signal: AbortSignal.timeout?.(5000),
  })
    .then((response) => response.json())
    .then((body) => parseBannerConfig(body.result))
    .catch(() => null)
  return bannerPromise
}

// Banner is client-only: hidden on SSR + first paint, then fetched and shown
// after mount if the campaign is live and the user hasn't dismissed it within
// the last 24h. Expiry is re-checked at render time so an open tab drops the
// banner once its endDate passes.
const useBannerState = (enabled: boolean) => {
  const [config, setConfig] = useState<BannerConfig | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    fetchBannerConfig().then((config) => {
      if (cancelled || !config || isExpired(config) || isDismissed(config)) {
        return
      }
      setConfig(config)
    })
    return () => {
      cancelled = true
    }
  }, [enabled])

  const dismiss = useCallback(() => {
    setConfig(null)
    if (!config) return
    try {
      localStorage.setItem(
        dismissKey(config),
        String(Date.now() + DISMISS_DURATION_MS)
      )
    } catch {
      // ignore — banner stays dismissed for the current session at least
    }
  }, [config])

  return { config: config && !isExpired(config) ? config : null, dismiss }
}

// Single entry point for SlitePage: returns the ready-to-render banner (or
// null) plus the offset the navbar should apply.
export const useBanner = (enabled = true) => {
  const { config, dismiss } = useBannerState(enabled)
  return {
    banner: config ? (
      <Banner
        title={config.title}
        description={config.description ?? undefined}
        link={config.link}
        linkLabel={config.linkLabel}
        onDismiss={dismiss}
      />
    ) : null,
    navbarTopOffset: config ? BANNER_HEIGHT : 0,
  }
}
